import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useKid } from "@/context/KidContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Field } from "@/pages/Login";
import { Plus, Trash2 } from "lucide-react";

export default function Profile() {
  const { user, logout } = useAuth();
  const { kids, reloadKids, setActive, activeId } = useKid();
  const [form, setForm] = useState({ name: "", birth_date: "" });
  const [saving, setSaving] = useState(false);

  const addChild = async (e) => {
    e.preventDefault();
    if (!form.name || !form.birth_date) return toast.error("Preencha nome e data de nascimento.");
    setSaving(true);
    try {
      const child = await api.post("/children", form).then(r => r.data);
      await reloadKids();
      setActive(child.id);
      setForm({ name: "", birth_date: "" });
      toast.success(`${child.name} cadastrado(a)!`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const removeChild = async (id) => {
    if (!window.confirm("Remover esta criança?")) return;
    await api.delete(`/children/${id}`);
    await reloadKids();
    toast.success("Removido");
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
      <h1 className="text-3xl font-extrabold text-[#243B53]">Meu Perfil</h1>
      <p className="text-[#243B53]/70">Gerencie suas informações e suas crianças.</p>

      <section className="mt-6 card-soft p-6 flex flex-wrap items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-[#FFD54F] flex items-center justify-center text-2xl">👤</div>
        <div>
          <div className="font-extrabold text-[#243B53] text-lg" data-testid="profile-user-name">{user?.name}</div>
          <div className="text-sm text-[#243B53]/60">{user?.email}</div>
          <span className="chip mt-1 inline-block capitalize">{user?.role}</span>
        </div>
        <button data-testid="profile-logout-btn" onClick={logout} className="btn-outline ml-auto text-sm">Sair da conta</button>
      </section>

      <section className="mt-8">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-extrabold text-[#243B53]">Minhas crianças</h2>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {kids.map((k) => (
            <div key={k.id} className={`card-soft p-5 ${k.id === activeId ? "ring-2 ring-[#FFD54F]" : ""}`} data-testid={`child-card-${k.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#5DADE2]/20 flex items-center justify-center text-2xl">🧒</div>
                <div>
                  <div className="font-extrabold text-[#243B53]">{k.name}</div>
                  <div className="text-xs text-[#243B53]/60">{k.age} anos • Faixa {k.age_group}</div>
                </div>
                <button onClick={() => removeChild(k.id)} data-testid={`child-remove-${k.id}`} className="ml-auto text-red-500"><Trash2 size={18} /></button>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                <div className="bg-[#FFF9ED] rounded-xl py-2"><b>{k.stars || 0}</b><div>⭐</div></div>
                <div className="bg-[#FFF9ED] rounded-xl py-2"><b>{k.points || 0}</b><div>pts</div></div>
                <div className="bg-[#FFF9ED] rounded-xl py-2"><b>{k.streak_days || 0}</b><div>🔥</div></div>
              </div>
              <button onClick={() => setActive(k.id)} data-testid={`child-activate-${k.id}`} className="btn-outline w-full mt-3 text-sm">
                {k.id === activeId ? "Selecionada" : "Selecionar"}
              </button>
            </div>
          ))}
          <form onSubmit={addChild} className="card-soft p-5 space-y-3">
            <div className="font-extrabold text-[#243B53] flex items-center gap-2"><Plus size={18} /> Nova criança</div>
            <Field label="Nome" value={form.name} onChange={(v) => setForm(s => ({ ...s, name: v }))} testid="new-child-name-input" />
            <Field label="Data de nascimento" type="date" value={form.birth_date} onChange={(v) => setForm(s => ({ ...s, birth_date: v }))} testid="new-child-birth-input" />
            <button data-testid="new-child-submit-btn" disabled={saving} className="btn-primary w-full text-sm">{saving ? "Salvando…" : "Cadastrar"}</button>
          </form>
        </div>
      </section>
    </div>
  );
}
